import type { SupabaseClient } from "@supabase/supabase-js";

// Member invite data layer (CONSOLE-MEMBER-INVITE-FORM, migration 20260611204135). Reads:
// ONLY the hosted management_member_invites view - the raw account_member_invites table is
// closed to app clients. Writes: the two hosted RPCs (admin/member.manage gated server-side).
// Acceptance (accept_account_member_invite) is the invitee's own signed-in join path, not
// this admin form. support is intentionally not an invitable role.

export const MEMBER_INVITES_VIEW = "management_member_invites";
export const MEMBER_INVITES_SELECT =
  "invite_id,email,role,status,expires_at,created_at,updated_at,display_name,contact_phone";

// Customer-invitable roles (the hosted RPC rejects support).
export const INVITE_ROLES = ["guard", "supervisor", "admin"] as const;
export type InviteRole = (typeof INVITE_ROLES)[number];

export const MEMBER_INVITE_LIST_LIMIT = 200;

export interface MemberInviteRow {
  inviteId: string;
  email: string;
  displayName: string | null;
  contactPhone: string | null;
  role: string;
  status: string;
  expiresAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

function asString(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

/** Pending (and recent) invites for the account, newest first. Throws safe. */
export async function fetchMemberInvites(
  client: SupabaseClient,
  accountId: string,
): Promise<MemberInviteRow[]> {
  const { data, error } = await client
    .from(MEMBER_INVITES_VIEW)
    .select(MEMBER_INVITES_SELECT)
    .eq("account_id", accountId)
    .order("created_at", { ascending: false })
    .limit(MEMBER_INVITE_LIST_LIMIT);
  if (error !== null || !Array.isArray(data)) {
    throw new Error("Could not load member invites.");
  }
  const invites: MemberInviteRow[] = [];
  for (const raw of data as Array<Record<string, unknown>>) {
    const inviteId = asString(raw.invite_id);
    const email = asString(raw.email);
    if (inviteId === null || email === null) continue;
    invites.push({
      inviteId,
      email,
      displayName: asString(raw.display_name),
      contactPhone: asString(raw.contact_phone),
      role: asString(raw.role) ?? "guard",
      status: asString(raw.status) ?? "pending",
      expiresAt: asString(raw.expires_at),
      createdAt: asString(raw.created_at),
      updatedAt: asString(raw.updated_at),
    });
  }
  return invites;
}

export const SAFE_MEMBER_INVITE_FAILURE = "Couldn't complete that invite change - please try again.";

// Contract-stable, value-free sentences from the hosted invite RPCs (20260611204135).
const KNOWN_MEMBER_INVITE_FAILURES: ReadonlySet<string> = new Set([
  "Member management permission is required.",
  "Invite email is not valid.",
  "Invite role is not supported.",
  "Pending invite already exists for this email.",
  "Active account membership already exists for this email.",
  "Pending invite was not found for this email.",
  "Pending invite is expired.",
]);

export type MemberInviteOutcome =
  | { ok: true; inviteId: string | null }
  | { ok: false; reason: string };

async function runInviteWrite(
  client: SupabaseClient,
  fn: string,
  params: Record<string, unknown>,
): Promise<MemberInviteOutcome> {
  try {
    const { data, error } = await client.rpc(fn, params);
    if (error !== null) {
      const message = typeof error.message === "string" ? error.message : "";
      return {
        ok: false,
        reason: KNOWN_MEMBER_INVITE_FAILURES.has(message) ? message : SAFE_MEMBER_INVITE_FAILURE,
      };
    }
    const row = Array.isArray(data) ? (data[0] as Record<string, unknown> | undefined) : undefined;
    return { ok: true, inviteId: typeof row?.invite_id === "string" ? row.invite_id : null };
  } catch {
    return { ok: false, reason: SAFE_MEMBER_INVITE_FAILURE };
  }
}

export async function inviteAccountMember(
  client: SupabaseClient,
  accountId: string,
  email: string,
  role: InviteRole,
  displayName: string | null = null,
  contactPhone: string | null = null,
  expiresAt: string | null = null,
): Promise<MemberInviteOutcome> {
  const trimmedName = displayName?.trim() ?? "";
  const trimmedPhone = contactPhone?.trim() ?? "";
  return runInviteWrite(client, "invite_account_member", {
    p_account_id: accountId,
    p_email: email,
    p_role: role,
    p_expires_at: expiresAt,
    p_display_name: trimmedName.length > 0 ? trimmedName : null,
    p_contact_phone: trimmedPhone.length > 0 ? trimmedPhone : null,
  });
}

export async function revokeMemberInvite(
  client: SupabaseClient,
  accountId: string,
  inviteId: string,
): Promise<MemberInviteOutcome> {
  return runInviteWrite(client, "revoke_account_member_invite", {
    p_account_id: accountId,
    p_invite_id: inviteId,
  });
}
